import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { Popover } from 'antd';
import IconFont from '$/components/IconFont';
import Tooltip from '$/components/Tooltip';
import ModelSelectPopup from '$/components/ModelSelectPopup';
// import SeedanceAgreementModal from '$/components/SeedanceAgreementModal';
import { useLanguageStore } from '$/store/languageStore';
import { mtstatTrack } from '$/utils/mtstat';
// import { isSeedanceV2ModelValue, hasSeedanceV2Agreement } from '$/utils/seedanceAgreement';
import { InputPanelToolBarProps, UploadButtonItem } from './types';
import SkillSelectPopup from './SkillSelectPopup';
import RoleSelectPopup from './RoleSelectPopup';
import styles from './index.module.scss';

/**
 * 渲染输入面板底部工具栏，角色入口随语言资源更新，并通过外部注入承载业务列表与资产库实现。
 */
function InputPanelToolBar({
  disabled = false,
  // Upload
  showUpload = true,
  uploadButtonList,
  onUploadLimitToast,
  uploadPopupContent,
  uploadPopoverOpen,
  onUploadPopoverOpenChange,
  uploadPopoverAnchorElement,
  // Model
  showModelSelect,
  modelMap,
  usedModel,
  setUsedModel,
  modelSelectContent,
  modelTrackContext,
  // Skill
  showSkillSelect,
  skillCategories,
  skillList,
  selectedSkill,
  onSkillSelect,
  showSelectionNamesInTrigger = false,
  onSkillShow,
  onSkillClick,
  onSkillTabClick,
  skillPopupTitle,
  skillSelectContent,
  // Role
  showRoleSelect,
  roleList,
  selectedRoles,
  roleSelectDisabled,
  onRoleSelect,
  onRolePopupOpenChange,
  roleTrackContext,
  roleScopes,
  roleUnavailableMessage,
  roleEmptyMessage,
  roleSelectContent,
  // Submit
  isSubmitDisabled,
  onSubmit,
  renderSubmitButton,
  // Asset library
  assetLibraryVisible,
  onCloseAssetLibrary,
  renderAssetModalContent,
  onAssetItemClick,
  onAssetBatchConfirm,
  trackLocation = 'home_page',
}: InputPanelToolBarProps) {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const roleLabel = languageStore.smartcatTranslationText((text) => (
    showSelectionNamesInTrigger ? text.character_ip : text.robo_juese
  )) || '角色';

  // ─── Upload Popover ─────────────────────────────────────────────────────
  const [internalPopoverOpen, setInternalPopoverOpen] = useState(false);
  const popoverOpen = uploadPopoverOpen ?? internalPopoverOpen;
  const handlePopoverOpenChange = onUploadPopoverOpenChange ?? setInternalPopoverOpen;

  /** 外部按钮作为上传菜单锚点时，滚动或缩放会使坐标失效，直接收起以保持定位准确。 */
  useEffect(() => {
    if (!uploadPopoverAnchorElement || !popoverOpen) return undefined;
    const closePopover = () => handlePopoverOpenChange(false);
    document.addEventListener('scroll', closePopover, true);
    window.addEventListener('resize', closePopover);
    return () => {
      document.removeEventListener('scroll', closePopover, true);
      window.removeEventListener('resize', closePopover);
    };
  }, [handlePopoverOpenChange, popoverOpen, uploadPopoverAnchorElement]);

  // ─── Skill Popover ──────────────────────────────────────────────────────
  const [skillPopoverOpen, setSkillPopoverOpen] = useState(false);
  const [rolePopoverOpen, setRolePopoverOpen] = useState(false);
  const [modelPopoverOpen, setModelPopoverOpen] = useState(false);
  const exposedModelsRef = useRef(new Set<string>());

  /** 资产库全屏弹框打开时锁定根页面滚动，关闭或卸载后恢复调用方原有样式。 */
  useEffect(() => {
    if (!assetLibraryVisible || !renderAssetModalContent || typeof document === 'undefined') {
      return undefined;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousRootOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousRootOverflow;
    };
  }, [assetLibraryVisible, renderAssetModalContent]);

  /** 工具栏禁用时收起已打开浮层，避免发送中或加载中仍能操作选择入口。 */
  useEffect(() => {
    if (!disabled) return;
    handlePopoverOpenChange(false);
    setSkillPopoverOpen(false);
    setRolePopoverOpen(false);
    onRolePopupOpenChange?.(false);
    setModelPopoverOpen(false);
  }, [disabled, handlePopoverOpenChange, onRolePopupOpenChange]);

  // ─── Seedance 协议弹窗 ──────────────────────────────────────────────────
  // const [seedanceAgreementOpen, setSeedanceAgreementOpen] = useState(false);
  // const seedanceAgreementProceedRef = useRef<(() => void) | null>(null);

  const activeModel = useMemo(() => {
    if (!usedModel) return null;
    const activeType = usedModel.activeType || 'imageModel';
    return usedModel[activeType] || null;
  }, [usedModel]);
  const modelDisplayName = activeModel?.modelName
    || languageStore.smartcatTranslationText((v) => v.roboneo_model);

  // ─── 内置上传弹框渲染 ──────────────────────────────────────────────────
  function renderUploadPopup() {
    if (uploadPopupContent) return uploadPopupContent;
    if (!uploadButtonList?.length) return null;
    return (
      <div className={styles.uploadButtonList}>
        {uploadButtonList.map((item: UploadButtonItem) => (
          <div
            key={item.key}
            className={classNames(styles.uploadButtonItem, {
              [styles.disabled]: item.disabled,
            })}
            onClick={() => {
              if (item.disabled) {
                if (item.key === 'image' || item.key === 'video' || item.key === 'file') {
                  onUploadLimitToast?.(item.key as 'image' | 'video' | 'file');
                }
                return;
              }
              item.onClick();
              handlePopoverOpenChange(false);
            }}
          >
            <IconFont name={item.icon} fontSize={16} className={styles.icon} />
            <span className={styles.uploadButtonItemText}>{item.text}</span>
          </div>
        ))}
      </div>
    );
  }

  const uploadAnchorRect = uploadPopoverAnchorElement?.getBoundingClientRect();
  const anchoredUploadPopover = showUpload && uploadAnchorRect && typeof document !== 'undefined'
    ? createPortal(
      <Popover
        placement="topLeft"
        open={!disabled && popoverOpen}
        onOpenChange={(open) => {
          if (disabled) return;
          handlePopoverOpenChange(open);
        }}
        overlayInnerStyle={{ padding: '0px' }}
        overlayStyle={{ marginTop: 5 }}
        content={renderUploadPopup()}
        trigger="click"
        overlayClassName="resetPopoverAndArrow"
      >
        <span
          style={{
            position: 'fixed',
            top: uploadAnchorRect.top,
            left: uploadAnchorRect.left,
            width: uploadAnchorRect.width,
            height: uploadAnchorRect.height,
            pointerEvents: 'none',
          }}
        />
      </Popover>,
      document.body,
    )
    : null;

  /** 模型项按实际可见性上报，同次打开内按分类和模型去重。 */
  function trackModel(
    event: 'roboneo_input_model_show' | 'roboneo_input_model_click',
    params: Parameters<NonNullable<React.ComponentProps<typeof ModelSelectPopup>['onExposed']>>[0],
  ) {
    if (!modelTrackContext || !modelPopoverOpen || disabled) return;
    const tab = params.modelCategoryTitle === 'imageModel' ? 'image' : 'video';
    const modelValue = params.model?.modelValue || 'auto';
    if (event === 'roboneo_input_model_show') {
      const key = `${tab}:${modelValue}`;
      if (exposedModelsRef.current.has(key)) return;
      exposedModelsRef.current.add(key);
    }
    mtstatTrack(event, {
      location: modelTrackContext.location,
      room_id: modelTrackContext.roomId,
      tab,
      model_value: modelValue,
      model_name: params.model?.modelName || 'auto',
    });
  }

  /** 渲染内置模型选择弹框，模型选定后与 Skill 一样关闭当前浮层。 */
  function renderModelSelectPopup() {
    if (modelSelectContent) return modelSelectContent;
    if (!modelMap || !usedModel || !setUsedModel) return null;
    return (
      <ModelSelectPopup
        modelMap={modelMap}
        usedModel={usedModel}
        setUsedModel={setUsedModel}
        onExposed={(params) => trackModel('roboneo_input_model_show', params)}
        onModelClick={(params) => trackModel('roboneo_input_model_click', params)}
        variant={showSelectionNamesInTrigger ? 'inputPanel' : 'default'}
        onModelSelect={() => setModelPopoverOpen(false)}
      />
    );
  }

  /** 渲染内置 Skill 选择弹框，并透传业务埋点回调。 */
  function renderSkillSelectPopup() {
    if (skillSelectContent) return skillSelectContent;
    return (
      <SkillSelectPopup
        title={skillPopupTitle ?? languageStore.smartcatTranslationText((v) => v.skills)}
        categories={skillCategories || []}
        skillList={skillList || []}
        selectedSkill={selectedSkill}
        onShow={onSkillShow}
        onSkillClick={onSkillClick}
        onTabClick={onSkillTabClick}
        onSelect={(skill) => {
          onSkillSelect?.(skill);
          setSkillPopoverOpen(false);
        }}
      />
    );
  }

  /**
   * 渲染角色多选弹框，列表、选中态和加载状态均由 composer 控制。
   */
  function renderRoleSelectPopup() {
    if (roleSelectContent) return roleSelectContent;
    return (
      <RoleSelectPopup
        trackContext={roleTrackContext}
        roleList={roleList || []}
        selectedRoles={selectedRoles || []}
        disabled={roleSelectDisabled}
        onSelect={(roles) => onRoleSelect?.(roles)}
        scopes={roleScopes}
        unavailableMessage={roleUnavailableMessage}
        emptyMessage={roleEmptyMessage}
      />
    );
  }

  /** 清除工具栏入口中的已选 Skill，并阻止本次点击重新打开选择浮层。 */
  function handleSelectedSkillRemove(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    onSkillSelect?.(null);
    setSkillPopoverOpen(false);
  }

  /** 渲染提交入口，未启用首页模型选择时保持房间输入框原有 DOM 结构。 */
  function renderSubmitButtonNode() {
    if (renderSubmitButton) return renderSubmitButton();
    return (
      <div
        className={classNames(styles.submitButton, {
          [styles.inline]: showModelSelect,
          [styles.disabled]: isSubmitDisabled,
        })}
        onClick={onSubmit}
        aria-label="提交"
      >
        <IconFont name={showSelectionNamesInTrigger ? 'arrowUpFill' : 'arrowUpBlack'} fontSize={showSelectionNamesInTrigger ? 20 : 16} color="currentColor" />
      </div>
    );
  }

  return (
    <>
      <div className={classNames(styles.inputToolBar, { [styles.namedTools]: showSelectionNamesInTrigger })}>
        <div className={classNames(styles.modelSlot, {
          [styles.namedTools]: showSelectionNamesInTrigger,
        })}
        >
          {showUpload ? (
            <Tooltip
              title={languageStore.smartcatTranslationText(
                (v) => v.RoboNeo_Web_studio_tool_add,
              )}
              placement="top"
            >
              <Popover
                placement="topLeft"
                open={disabled || uploadPopoverAnchorElement ? false : popoverOpen}
                onOpenChange={(open) => {
                  if (disabled) return;
                  handlePopoverOpenChange(open);
                }}
                overlayInnerStyle={{ padding: '0px' }}
                overlayStyle={{ marginTop: 5 }}
                content={renderUploadPopup()}
                trigger={disabled ? [] : 'click'}
                overlayClassName="resetPopoverAndArrow"
              >
                <div className={classNames(styles.upload, {
                  [styles.disabled]: disabled,
                  [styles.namedTools]: showSelectionNamesInTrigger,
                })}
                >
                  <IconFont name={showSelectionNamesInTrigger ? 'plusFill' : 'plusBold'} fontSize={showSelectionNamesInTrigger ? 18 : 14} className={styles.icon} />
                </div>
              </Popover>
            </Tooltip>
          ) : null}

          {showSkillSelect ? (
            <Popover
              placement="topLeft"
              open={disabled ? false : skillPopoverOpen}
              onOpenChange={(open) => {
                if (disabled) return;
                setSkillPopoverOpen(open);
              }}
              overlayInnerStyle={{ padding: '0px' }}
              overlayStyle={{ padding: 0, paddingBottom: '4px' }}
              content={renderSkillSelectPopup()}
              trigger={disabled ? [] : 'click'}
              overlayClassName="resetPopoverAndArrow"
            >
              <Tooltip
                title={languageStore.smartcatTranslationText(
                  (v) => v.skills,
                )}
                placement="top"
              >
                <div
                  className={classNames(styles.skillButton, {
                    [styles.active]: !!selectedSkill,
                    [styles.disabled]: disabled,
                    [styles.namedToolButton]: showSelectionNamesInTrigger,
                    [styles.deletable]: showSelectionNamesInTrigger && !!selectedSkill,
                  })}
                  onClick={() => {
                    if (disabled) return;
                    mtstatTrack('roboneo_input_box_click', { icon_type: 'skill' });
                  }}
                >
                  {showSelectionNamesInTrigger ? (
                    <>
                      <span className={styles.toolIconSlot}>
                        <IconFont name="bookBold" fontSize={18} className={styles.toolIcon} />
                        {selectedSkill ? (
                          <button
                            type="button"
                            className={styles.toolRemoveButton}
                            onClick={handleSelectedSkillRemove}
                          >
                            <IconFont name="crossFill" fontSize={8} />
                          </button>
                        ) : null}
                      </span>
                      {selectedSkill ? <span className={styles.toolName}>{selectedSkill.name}</span> : null}
                    </>
                  ) : (
                    <IconFont name="bookBold" fontSize={14} className={styles.icon} />
                  )}
                </div>
              </Tooltip>
            </Popover>
          ) : null}

          {showRoleSelect ? (
            <Popover
              placement="topLeft"
              open={disabled ? false : rolePopoverOpen}
              onOpenChange={(open) => {
                if (disabled) return;
                setRolePopoverOpen(open);
                onRolePopupOpenChange?.(open);
              }}
              overlayInnerStyle={{ padding: 0 }}
              overlayStyle={{ padding: 0, paddingBottom: 4 }}
              content={renderRoleSelectPopup()}
              trigger={disabled ? [] : 'click'}
              overlayClassName="resetPopoverAndArrow"
            >
              <Tooltip title={roleLabel} placement="top">
                <div
                  onClick={() => {
                    if (disabled || !roleTrackContext) return;
                    mtstatTrack('roboneo_input_box_click', {
                      icon_type: 'ip',
                      room_id: roleTrackContext.roomId,
                      page_url: window.location.href,
                      ...(roleTrackContext.projectId ? { project_id: roleTrackContext.projectId } : {}),
                    });
                  }}
                  className={classNames(showSelectionNamesInTrigger ? styles.namedToolButton : styles.skillButton, {
                    [styles.roleButton]: !showSelectionNamesInTrigger,
                    [styles.roleNamedButton]: showSelectionNamesInTrigger,
                    [styles.active]: !!selectedRoles?.length,
                    [styles.disabled]: disabled,
                  })}
                >
                  <IconFont name="portraitPhotoBold" fontSize={showSelectionNamesInTrigger ? 18 : 14} className={styles.toolIcon} />
                  {showSelectionNamesInTrigger ? <span className={styles.toolName}>{roleLabel}</span> : null}
                </div>
              </Tooltip>
            </Popover>
          ) : null}
        </div>

        {showModelSelect ? (
          <div className={styles.submitSlot}>
            <Popover
              placement="topRight"
              open={disabled ? false : modelPopoverOpen}
              onOpenChange={(open) => {
                if (disabled) return;
                if (open) exposedModelsRef.current.clear();
                setModelPopoverOpen(open);
              }}
              overlayInnerStyle={{ padding: 0 }}
              overlayStyle={{ padding: 0, paddingBottom: 4 }}
              // 始终提供内容函数，避免空 content 导致 Popover 拦截首次打开回调。
              content={() => (modelPopoverOpen && !disabled ? renderModelSelectPopup() : null)}
              trigger={disabled ? [] : 'click'}
              overlayClassName="resetPopoverAndArrow"
            >
              <Tooltip
                title={languageStore.smartcatTranslationText((v) => v.roboneo_model_change)}
                placement="top"
              >
                <div
                  className={classNames(styles.namedToolButton, {
                    [styles.disabled]: disabled,
                  })}
                  onClick={() => {
                    if (disabled) return;
                    mtstatTrack('roboneo_input_box_click', { icon_type: 'model' });
                  }}
                >
                  <span className={styles.toolIconSlot}>
                    <IconFont name={showSelectionNamesInTrigger ? 'aiModelAutoBold' : 'aiModelBold'} fontSize={showSelectionNamesInTrigger ? 18 : 14} className={styles.toolIcon} />
                  </span>
                  <span className={styles.toolName}>{modelDisplayName}</span>
                </div>
              </Tooltip>
            </Popover>
            {renderSubmitButtonNode()}
          </div>
        ) : renderSubmitButtonNode()}
      </div>
      {anchoredUploadPopover}

      {/* <SeedanceAgreementModal
        open={seedanceAgreementOpen}
        onClose={() => setSeedanceAgreementOpen(false)}
        onAgree={() => {
          setSeedanceV2Agreement();
          setSeedanceAgreementOpen(false);
          seedanceAgreementProceedRef.current?.();
          seedanceAgreementProceedRef.current = null;
        }}
      /> */}

      {assetLibraryVisible && renderAssetModalContent && createPortal(
        <div className={styles.assetLibraryOverlay} role="presentation">
          <button
            type="button"
            className={styles.assetLibraryBackdrop}
            aria-label="关闭资产库"
            onClick={onCloseAssetLibrary}
          />

          {renderAssetModalContent({
            setOpenAssetPopover: () => { onCloseAssetLibrary?.(); },
            trackLocation,
            onAssetItemClick: (item) => { onAssetItemClick?.(item); },
            onAssetBatchConfirm: (items) => { onAssetBatchConfirm?.(items); },
            enableAddToAssetLibrary: true,
          })}
        </div>,
        document.body,
      )}
    </>
  );
}

export default React.memo(InputPanelToolBar);
